import React, { ReactNode, useEffect, useMemo, useState } from "react";
import { MarkedText } from "../markedText";
import styles from "./checkboxGroup.module.css";

export interface CheckboxGroupOption {
  key: string;
  label: string;
}

interface CheckboxGroupProps {
  options: CheckboxGroupOption[];
  onChange: (keys: string[]) => void;
  value: string[];
  checkAllLabel?: string | ReactNode;
  renderOptionLabel?: (option: CheckboxGroupOption, currentElement: ReactNode) => ReactNode;
  sortOptions?: (options: CheckboxGroupOption[]) => CheckboxGroupOption[];
  headerExtraLeft?: ReactNode;
}

const initialState = {};
export const CheckboxGroup = ({
  options,
  onChange,
  value,
  renderOptionLabel,
  checkAllLabel = "Check All",
  sortOptions,
  headerExtraLeft,
}: CheckboxGroupProps) => {
  const [checkedOptions, setCheckedOptions] = useState<Record<string, boolean>>(initialState);
  const [lastPropsOptions, setLastPropsOptions] = useState<Record<string, boolean>>({});
  const [searchQuery, setSearchQuery] = useState<string>("");

  useEffect(() => {
    const result: Record<string, boolean> = {};
    value.forEach((key) => {
      result[key] = true;
    });

    setLastPropsOptions(result);
    setCheckedOptions(result);
  }, [value]);

  useEffect(() => {
    if (initialState !== checkedOptions && lastPropsOptions !== checkedOptions) {
      onChange(Object.keys(checkedOptions));
    }
  }, [checkedOptions, lastPropsOptions]);

  const inputOnChange = (key: string) => {
    return (e: React.ChangeEvent<HTMLInputElement>) => {
      const checked = e.target.checked;

      setCheckedOptions((prev) => {
        const cloned = { ...prev };

        if (checked) {
          cloned[key] = true;
        } else {
          delete cloned[key];
        }

        return cloned;
      });
    };
  };

  const filterOptionBySearchQuery = (option: CheckboxGroupOption) => {
    return option.label.toLowerCase().indexOf(searchQuery.toLowerCase()) !== -1;
  };

  const onCheckAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;

    if (checked) {
      const result: Record<string, boolean> = {};
      options.filter(filterOptionBySearchQuery).forEach((item) => {
        result[item.key] = true;
      });
      setCheckedOptions(result);
    } else {
      setCheckedOptions({});
    }
  };

  const onSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const isVisibleOption = (option: string) => {
    return option.toLowerCase().indexOf(searchQuery.toLowerCase()) !== -1;
  };

  const sort = useMemo(() => {
    return sortOptions ? sortOptions : (options: CheckboxGroupOption[]) => options;
  }, [sortOptions]);

  const optionsComponent = useMemo(() => {
    return (
      <>
        {sort(options).map((option) => (
          <div
            className={styles.option}
            key={`${option.key}-${searchQuery}`}
            style={{ display: isVisibleOption(option.key) ? "block" : "none" }}
          >
            <label className={styles.optionLabel}>
              <input
                type="checkbox"
                checked={checkedOptions[option.key] || false}
                onChange={inputOnChange(option.key)}
              />
              {renderOptionLabel ? (
                renderOptionLabel(option, <MarkedText textToMark={searchQuery}>{option.label}</MarkedText>)
              ) : (
                <MarkedText textToMark={searchQuery}>{option.label}</MarkedText>
              )}
            </label>
          </div>
        ))}
      </>
    );
  }, [options, searchQuery]);

  return (
    <>
      <div className={styles.header}>
        <input className={styles.searchInput} type="text" placeholder="Search by query" onChange={onSearch} />
        {headerExtraLeft}
      </div>

      <div className={styles.option}>
        <label className={styles.optionLabel}>
          <input
            checked={options.filter(filterOptionBySearchQuery).length === Object.keys(checkedOptions).length}
            type="checkbox"
            onChange={onCheckAll}
          />
          {checkAllLabel}
        </label>
      </div>
      <div style={{ maxHeight: 300, overflowY: "auto" }}>{optionsComponent}</div>
    </>
  );
};
